import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons"
import { Alert, Button, Flex, Form, Grid, Modal, Steps } from "antd"
import React, { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { type BlockerFunction, useNavigate } from "react-router-dom"

import ReactRouterPrompt from "../../../../components/ReactRouterPrompt.tsx"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { LEAD_SOURCE_NONE } from "../../../Leads/constants/lead-source-none.ts"
import type { LeadsFilterOptions } from "../../../Leads/redux/leads.types.ts"
import { createLead } from "../../redux/lead.thunks.ts"
import type { LeadFormData } from "../components/lead-form-data.ts"
import { LeadWizardAbandonConfirm } from "./LeadWizardAbandonConfirm.tsx"
import { CUSTOMER_SEARCH_INPUT_ID } from "./LeadWizardCustomerSearch.tsx"
import { LeadWizardCustomerStep } from "./LeadWizardCustomerStep.tsx"
import { LeadWizardProjectStep } from "./LeadWizardProjectStep.tsx"
import { createLeadErrorMessage } from "./create-lead-error.ts"
import { buildCreateLeadPayload } from "./create-lead-payload.ts"
import { type CustomerMatchCandidate, candidateKey } from "./customer-match.ts"
import {
  type LinkedCustomer,
  resolveLinkedCustomer
} from "./linked-customer.ts"

const CUSTOMER_STEP = 0
const PROJECT_STEP = 1

const MODAL_WIDTH = 800

const INITIAL_VALUES: LeadFormData = { source: LEAD_SOURCE_NONE }

interface CreateLeadWizardProps {
  options: LeadsFilterOptions
  saving?: boolean
}

export function CreateLeadWizard({ options, saving }: CreateLeadWizardProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [form] = Form.useForm<LeadFormData>()
  const screens = Grid.useBreakpoint()

  const [step, setStep] = useState(CUSTOMER_STEP)
  const [linked, setLinked] = useState<LinkedCustomer | null>(null)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Read by the router blocker, which runs inside the same tick as navigate();
  // a state flag would still be false when it decides whether to stop us.
  const leaving = useRef(false)
  const [abandoning, setAbandoning] = useState(false)

  const fullScreen = !screens.md

  const values = Form.useWatch([], form)
  // Anything the user typed is lost on leave, so the guard has to answer to the
  // whole form rather than to the customer name alone.
  const hasEnteredData = useMemo(() => {
    if (!values) return false

    return Object.entries(values).some(([key, value]) => {
      if (key === "source") return value !== LEAD_SOURCE_NONE
      if (Array.isArray(value)) return value.length > 0
      if (value && typeof value === "object") {
        return Object.values(value).some((nested) => !!nested)
      }
      return !!value
    })
  }, [values])

  const blockNavigation: BlockerFunction = ({
    currentLocation,
    nextLocation
  }) => {
    if (leaving.current || !hasEnteredData) return false
    return currentLocation.pathname !== nextLocation.pathname
  }

  const leaveToList = () => {
    leaving.current = true
    navigate(RoutesPaths.lead.list)
  }

  // Every close affordance lands here: the X, the mask, Escape and Cancel.
  const requestClose = () => {
    if (!hasEnteredData) {
      leaveToList()
      return
    }

    setAbandoning(true)
  }

  const discard = () => {
    setAbandoning(false)
    leaving.current = true
    form.resetFields()
    navigate(RoutesPaths.lead.list)
  }

  const onUse = async (candidate: CustomerMatchCandidate) => {
    setBusyKey(candidateKey(candidate))

    try {
      const resolved = await resolveLinkedCustomer(candidate)
      setLinked(resolved)
      form.setFieldValue("customer", resolved.customer)
    } finally {
      setBusyKey(null)
    }
  }

  // The router blocker reads `leaving` in the same tick as navigate(), so
  // abandoning the form for an existing lead has to be declared here rather
  // than navigated to from the step, or the guard treats it as a stray exit.
  const openExistingLead = (id: string) => {
    leaving.current = true
    navigate(RoutesPaths.lead.view(id))
  }

  // The spec's second way out: save the lead as though the warning had never
  // appeared. Dismissing alone left the user to press Create again.
  const createAnyway = () => {
    form.submit()
  }

  const onCreateNew = () => {
    setLinked(null)
    form.setFieldValue("use_customer_address", false)
  }

  const onSubmit = async (submitted: LeadFormData) => {
    setError(null)

    try {
      const lead = await dispatch(
        createLead(buildCreateLeadPayload(submitted, linked))
      ).unwrap()

      if (!lead?.id) throw new Error("Lead id is not defined")

      leaving.current = true
      navigate(RoutesPaths.lead.view(lead.id))
    } catch (rejection) {
      setError(
        createLeadErrorMessage(rejection, t("leadWizard.error.fallback"))
      )
    }
  }

  const onSubmitFailed = ({
    errorFields
  }: {
    errorFields: { name: unknown[] }[]
  }) => {
    const firstErrored = errorFields?.[0]?.name?.[0]
    setStep(firstErrored === "customer" ? CUSTOMER_STEP : PROJECT_STEP)
  }

  // Enter mirrors Next rather than submitting: a submit button in this form
  // would let Enter create the lead outright, past step 2.
  const onKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== "Enter" || step !== CUSTOMER_STEP) return

    // The search box is live, so Enter there is the user asking to search.
    if ((event.target as HTMLElement).id === CUSTOMER_SEARCH_INPUT_ID) return

    event.preventDefault()
    setStep(PROJECT_STEP)
  }

  const footer =
    step === CUSTOMER_STEP ? (
      <Flex gap={"small"} wrap justify={"space-between"}>
        <Button className={"min-h-11 max-sm:w-full"} onClick={requestClose}>
          {t("leadWizard.actions.cancel")}
        </Button>
        <Button
          key={"wizard-next"}
          type={"primary"}
          icon={<ArrowRightOutlined aria-hidden />}
          iconPosition={"end"}
          className={"min-h-11 max-sm:w-full"}
          onClick={() => setStep(PROJECT_STEP)}
        >
          {t("leadWizard.actions.next")}
        </Button>
      </Flex>
    ) : (
      <Flex gap={"small"} wrap justify={"space-between"}>
        <Button
          key={"wizard-back"}
          icon={<ArrowLeftOutlined aria-hidden />}
          className={"min-h-11 max-sm:w-full"}
          onClick={() => setStep(CUSTOMER_STEP)}
        >
          {t("leadWizard.actions.back")}
        </Button>
        <Flex gap={"small"} wrap className={"max-sm:w-full"}>
          <Button className={"min-h-11 max-sm:flex-1"} onClick={requestClose}>
            {t("leadWizard.actions.cancel")}
          </Button>
          <Button
            key={"wizard-create-primary"}
            type={"primary"}
            loading={saving}
            disabled={saving}
            className={"min-h-11 max-sm:flex-1"}
            onClick={() => form.submit()}
          >
            {t("leadWizard.actions.create")}
          </Button>
        </Flex>
      </Flex>
    )

  return (
    <>
      <Modal
        open
        centered={!fullScreen}
        width={fullScreen ? "100vw" : MODAL_WIDTH}
        title={t("leadWizard.title")}
        onCancel={requestClose}
        footer={footer}
        style={
          fullScreen
            ? { top: 0, maxWidth: "100vw", margin: 0, paddingBottom: 0 }
            : undefined
        }
        styles={{
          content: fullScreen
            ? { borderRadius: 0, minHeight: "100dvh" }
            : {
                display: "flex",
                flexDirection: "column",
                minHeight: "min(820px, calc(100dvh - 48px))",
                maxHeight: "calc(100dvh - 48px)"
              },
          // The body is the only scrolling box, which is what keeps the footer
          // reading as the sticky action bar the page version had.
          body: {
            flex: 1,
            maxHeight: "calc(100dvh - 190px)",
            overflowX: "hidden",
            overflowY: "auto"
          }
        }}
      >
        <Form
          form={form}
          layout={"vertical"}
          disabled={saving}
          initialValues={INITIAL_VALUES}
          onFinish={onSubmit}
          onFinishFailed={onSubmitFailed}
          onKeyDown={onKeyDown}
          data-testid={"create-lead-wizard"}
          className={"w-full with-default-error"}
        >
          <Steps
            responsive
            current={step}
            className={"mx-auto mb-8 max-w-xl"}
            onChange={setStep}
            items={[
              { title: t("leadWizard.steps.customer") },
              { title: t("leadWizard.steps.project") }
            ]}
          />

          {error && (
            <Alert
              type={"error"}
              showIcon
              closable
              className={"mb-4"}
              message={t("leadWizard.error.title")}
              description={error}
              onClose={() => setError(null)}
            />
          )}

          {/* Both steps stay mounted: an unmounted Form.Item registers no rules,
              so Create Lead on step 2 would not see a missing customer name. */}
          <div hidden={step !== CUSTOMER_STEP}>
            <LeadWizardCustomerStep
              form={form}
              linked={linked}
              busyKey={busyKey}
              onUse={onUse}
              onOpenExistingLead={openExistingLead}
              onCreateNew={onCreateNew}
              onCreateAnyway={createAnyway}
            />
          </div>
          <div hidden={step !== PROJECT_STEP}>
            <LeadWizardProjectStep
              form={form}
              options={options}
              customerAddress={linked?.address ?? null}
            />
          </div>
        </Form>
      </Modal>

      {abandoning && (
        <LeadWizardAbandonConfirm
          open
          onCancel={() => setAbandoning(false)}
          onOk={discard}
        />
      )}

      <ReactRouterPrompt when={blockNavigation}>
        {({ isActive, onConfirm, onCancel }) => (
          <LeadWizardAbandonConfirm
            open={isActive}
            onOk={onConfirm}
            onCancel={onCancel}
          />
        )}
      </ReactRouterPrompt>
    </>
  )
}
